import React, { useState } from "react";
import { Box, Card, CardContent, Grid, Typography, TextField, Button, Stack, InputAdornment, LinearProgress, Checkbox, FormControlLabel, IconButton, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useTranslation } from "react-i18next";
import axios from "axios";

function LoginRegisterForm({
  setToken,
  showMessage,
  serverUrl,
  requireEmailVerification = true,
  ldapEnabled = false,
}) {
  const { t } = useTranslation();
  const [loginUsername, setLoginUsername] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [registerUsername, setRegisterUsername] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [resendEmail, setResendEmail] = useState("");

  // modal visibility for forgot password
  const [forgotOpen, setForgotOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(true);

  // real‑time validation state
  const [loginUsernameError, setLoginUsernameError] = useState("");
  const [loginPasswordError, setLoginPasswordError] = useState("");
  const [registerUsernameError, setRegisterUsernameError] = useState("");
  const [registerPasswordError, setRegisterPasswordError] = useState("");
  const [registerEmailError, setRegisterEmailError] = useState("");
  const [forgotEmailError, setForgotEmailError] = useState("");
  const [resendEmailError, setResendEmailError] = useState("");

  // ----- password strength -----
  const [passwordStrength, setPasswordStrength] = useState(0);
  const calculateStrength = (pw) => {
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    return (score / 4) * 100;
  };

  // additional UI state for login experience
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState({
    login: false,
    register: false,
    forgot: false,
    resend: false,
  });

  const handleLogin = () => {
    if (!loginUsername || !loginPassword) {
      showMessage(t("loginMissingFields"));
      return;
    }
    setLoading((prev) => ({ ...prev, login: true }));
    axios
      .post(`${serverUrl}/login`, {
        username: loginUsername,
        password: loginPassword,
      })
      .then((res) => {
        const tkn = res.data.token;
        setToken(tkn);
        localStorage.setItem("token", tkn);
      })
      .catch((error) => {
        const message = error?.response?.data?.message || t("loginFailed");
        showMessage(message);
      })
      .finally(() => setLoading((prev) => ({ ...prev, login: false })));
  };

  const handleRegister = () => {
    if (!registerEmail || !registerEmail.includes("@")) {
      showMessage(t("registrationInvalidEmail"));
      return;
    }
    if (!registerUsername || !registerPassword) {
      showMessage(t("registrationMissingFields"));
      return;
    }
    setLoading((prev) => ({ ...prev, register: true }));
    const successKey = requireEmailVerification
      ? "registrationSuccess"
      : "registrationSuccessImmediate";
    axios
      .post(`${serverUrl}/register`, {
        username: registerUsername,
        password: registerPassword,
        email: registerEmail,
      })
      .then(() => {
        showMessage(t(successKey));
        setRegisterPassword("");
      })
      .catch((error) => {
        const message =
          error?.response?.data?.message || t("registrationFailed");
        showMessage(message);
      })
      .finally(() => setLoading((prev) => ({ ...prev, register: false })));
  };

  const handleForgotPassword = () => {
    if (!forgotEmail) {
      showMessage(t("forgotPasswordMissingEmail"));
      return;
    }
    setLoading((prev) => ({ ...prev, forgot: true }));
    axios
      .post(`${serverUrl}/forgot-password`, { email: forgotEmail })
      .then((res) =>
        showMessage(
          res?.data?.message || t("forgotPasswordEmailSent")
        )
      )
      .catch((error) => {
        const message =
          error?.response?.data?.message || t("forgotPasswordEmailError");
        showMessage(message);
      })
      .finally(() => setLoading((prev) => ({ ...prev, forgot: false })));
  };

  // ----- Real‑time validation helpers -----
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleLoginUsernameChange = (e) => {
    const val = e.target.value;
    setLoginUsername(val);
    setLoginUsernameError(val ? "" : t("fieldRequired"));
  };
  const handleLoginPasswordChange = (e) => {
    const val = e.target.value;
    setLoginPassword(val);
    setLoginPasswordError(val ? "" : t("fieldRequired"));
  };
  const handleRegisterUsernameChange = (e) => {
    const val = e.target.value;
    setRegisterUsername(val);
    setRegisterUsernameError(val ? "" : t("fieldRequired"));
  };
  const handleRegisterPasswordChange = (e) => {
    const val = e.target.value;
    setRegisterPassword(val);
    const err = val && val.length >= 6 ? "" : t("passwordTooShort");
    setRegisterPasswordError(err);
    setPasswordStrength(calculateStrength(val));
  };
  const handleRegisterEmailChange = (e) => {
    const val = e.target.value;
    setRegisterEmail(val);
    setRegisterEmailError(
      val && emailRegex.test(val) ? "" : t("invalidEmail")
    );
  };
  const handleForgotEmailChange = (e) => {
    const val = e.target.value;
    setForgotEmail(val);
    setForgotEmailError(
      val && emailRegex.test(val) ? "" : t("invalidEmail")
    );
  };
  const handleResendEmailChange = (e) => {
    const val = e.target.value;
    setResendEmail(val);
    setResendEmailError(
      val && emailRegex.test(val) ? "" : t("invalidEmail")
    );
  };

  // derived form validity flags
  const loginValid =
    loginUsername && loginPassword && !loginUsernameError && !loginPasswordError;
  const registerValid =
    registerUsername &&
    registerPassword &&
    registerEmail &&
    !registerUsernameError &&
    !registerPasswordError &&
    !registerEmailError;

  const handleResendVerification = () => {
    if (!resendEmail) {
      showMessage(t("resendVerificationMissingEmail"));
      return;
    }
    setLoading((prev) => ({ ...prev, resend: true }));
    axios
      .post(`${serverUrl}/resend-verification`, { email: resendEmail })
      .then((res) =>
        showMessage(
          res?.data?.message || t("resendVerificationSuccess")
        )
      )
      .catch((error) => {
        const message =
          error?.response?.data?.message || t("resendVerificationError");
        showMessage(message);
      })
      .finally(() => setLoading((prev) => ({ ...prev, resend: false })));
  };

  const registrationHintKey = requireEmailVerification
    ? "registrationEmailHint"
    : "registrationEmailOptionalHint";

  return (
    <Box display="flex" justifyContent="center" mt={4}>
      <Card sx={{ maxWidth: 400, width: "100%", p: 2 }} elevation={3}>
        <CardContent>
          <Grid spacing={4} direction="row">
            {/* Login Section */}
            {showLogin && (
            <Grid item xs={12} sm={6}>
              <Typography variant="h5" gutterBottom>
                {t("login")}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                {t("welcomeBack")}
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label={t("username")}
                  fullWidth
                  autoFocus
                  value={loginUsername}
                  onChange={handleLoginUsernameChange}
                  onKeyPress={(e) => { if (e.key === "Enter") handleLogin(); }}
                  error={!!loginUsernameError}
                  helperText={loginUsernameError}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label={t("password")}
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  value={loginPassword}
                  onChange={handleLoginPasswordChange}
                  error={!!loginPasswordError}
                  helperText={loginPasswordError}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {/* Remember me */}
                {/* <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={t("rememberMe")}
                /> */}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleLogin}
                  fullWidth
                  disabled={loading.login || !loginValid}
                >
                  {loading.login ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t("login")
                  )}
                </Button>
                {/* Forgot Password link */}
                {/* <Button
                  variant="text"
                  onClick={() => setForgotOpen(true)}
                  sx={{ mt: 1, textTransform: "none" }}
                >
                  {t("forgotPasswordTitle")}
                </Button> */}
                {/* Toggle to Register */}
                <Button
                  variant="text"
                  onClick={() => setShowLogin(false)}
                  sx={{ mt: 1, textTransform: "none" }}
                >
                  {t("dontHaveAccount")}
                </Button>
                {/* Forgot Password Modal */}
                {forgotOpen && (
                  <Dialog open={true} onClose={() => setForgotOpen(false)} maxWidth="xs" fullWidth>
                    <DialogTitle>{t("forgotPasswordTitle")}</DialogTitle>
                    <DialogContent dividers>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t("forgotPasswordDescription")}
                      </Typography>
                      <TextField
                        label={t("email")}
                        type="email"
                        fullWidth
                        value={forgotEmail}
                        onChange={handleForgotEmailChange}
                        error={!!forgotEmailError}
                        helperText={forgotEmailError}
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                      {ldapEnabled && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                          {t("forgotPasswordLdapNotice")}
                        </Typography>
                      )}
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setForgotOpen(false)}>{t("cancel")}</Button>
                      <Button
                        variant="outlined"
                        onClick={handleForgotPassword}
                        disabled={loading.forgot || !!forgotEmailError}
                      >
                        {t("forgotPasswordButton")}
                      </Button>
                    </DialogActions>
                  </Dialog>
                )}
              </Stack>
            </Grid>
            )}

            {/* Register Section */}
            {!showLogin && (
            <Grid item xs={12} sm={6}>
              <Typography variant="h5" gutterBottom>
                {t("register")}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                {t("createYourAccount")}
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label={t("name")}
                  fullWidth
                  value={registerUsername}
                  onChange={handleRegisterUsernameChange}
                  error={!!registerUsernameError}
                  helperText={registerUsernameError}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label={t("password")}
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  value={registerPassword}
                  onChange={handleRegisterPasswordChange}
                  error={!!registerPasswordError}
                  helperText={registerPasswordError}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {/* Password strength */}
                <LinearProgress variant="determinate" value={passwordStrength} sx={{ height: 8, borderRadius: 4, mt: 1 }} />
                <TextField
                  label={t("email")}
                  type="email"
                  fullWidth
                  value={registerEmail}
                  onChange={handleRegisterEmailChange}
                  error={!!registerEmailError}
                  helperText={registerEmailError}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleRegister}
                  fullWidth
                  disabled={loading.register || !registerValid}
                >
                  {loading.register ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t("register")
                  )}
                </Button>
                {/* <Typography variant="body2" color="text.secondary">
                  {t(registrationHintKey)}
                </Typography> */}
                {/* Resend Verification */}
                {requireEmailVerification && (
                  <Stack spacing={1} mt={3}>
                    <Typography variant="subtitle2">
                      {t("resendVerificationTitle")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("resendVerificationDescription")}
                    </Typography>
                    <TextField
                      label={t("email")}
                      type="email"
                      fullWidth
                      value={resendEmail}
                      onChange={handleResendEmailChange}
                      error={!!resendEmailError}
                      helperText={resendEmailError}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleResendVerification}
                      disabled={loading.resend || !!resendEmailError}
                    >
                      {t("resendVerificationButton")}
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}

export default LoginRegisterForm;
